import React from 'react';
import { useAttachments } from '@/context/AttachmentContext';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart3, FileCog, Clock, Briefcase } from 'lucide-react';
import { simplifyFileType } from '@/utils/fileTypeUtils';
import { formatDistanceToNow } from 'date-fns';

const AttachmentInsights: React.FC = () => {
  const { filteredAttachments } = useAttachments();

  // Calculate storage insights
  const getTotalStorageInfo = () => {
    if (!filteredAttachments.length) return { size: 0, formatted: '0 MB' };
    
    const totalSize = filteredAttachments.reduce((acc, att) => acc + att.fileSize, 0);
    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
    return { 
      size: totalSize, 
      formatted: `${totalSizeMB} MB` 
    };
  };

  // Calculate attachment type breakdown
  const getFileTypeInsight = () => {
    if (!filteredAttachments.length) return { type: 'None', count: 0, percentage: 0 };
    
    const typeCounts: Record<string, number> = {};
    filteredAttachments.forEach(att => {
      const simpleType = simplifyFileType(att.fileType, att.fileName);
      typeCounts[simpleType] = (typeCounts[simpleType] || 0) + 1;
    });
    
    const mostCommonType = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])[0];
      
    return { 
      type: mostCommonType[0].toUpperCase(), 
      count: mostCommonType[1],
      percentage: Math.round((mostCommonType[1] / filteredAttachments.length) * 100)
    };
  };

  // Calculate time-based insights
  const getTimeInsight = () => {
    if (!filteredAttachments.length) return { recent: 0, lastUpdated: 'No data' };
    
    // Count files from last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentFiles = filteredAttachments.filter(att => 
      new Date(att.emailDate) >= thirtyDaysAgo
    ).length;
    
    // Most recent file
    const mostRecentFile = [...filteredAttachments]
      .sort((a, b) => new Date(b.emailDate).getTime() - new Date(a.emailDate).getTime())[0];
    
    return { 
      recent: recentFiles,
      lastUpdated: mostRecentFile ? formatDistanceToNow(new Date(mostRecentFile.emailDate), { addSuffix: true }) : 'No data'
    };
  };

  // Calculate productivity insights
  const getWorkInsight = () => {
    if (!filteredAttachments.length) return { docCount: 0, workPercentage: 0 };
    
    // Calculate document count (PDFs, office files)
    const docCount = filteredAttachments.filter(att => 
      ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(simplifyFileType(att.fileType, att.fileName))
    ).length;
    
    // Calculate percentage of work files
    const workPercentage = Math.round((docCount / filteredAttachments.length) * 100);
    
    return { docCount, workPercentage };
  };

  const storage = getTotalStorageInfo();
  const fileTypes = getFileTypeInsight();
  const timeInfo = getTimeInsight();
  const workInfo = getWorkInsight();

  if (!filteredAttachments.length) {
    return (
      <Card className="bg-transparent border-none shadow-none">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">No attachments found. Add some attachments to see insights.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-transparent border-none shadow-none">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Storage Usage */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border">
            <div className="p-2 rounded-full bg-blue-50 dark:bg-blue-900/30">
              <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Storage Used</p>
              <p className="text-base font-medium">{storage.formatted}</p>
            </div>
          </div>
          
          {/* Total Files */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border">
            <div className="p-2 rounded-full bg-purple-50 dark:bg-purple-900/30">
              <FileCog className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Files</p>
              <p className="text-base font-medium">{filteredAttachments.length}</p>
            </div>
          </div>
          
          {/* Time-based Analysis */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border">
            <div className="p-2 rounded-full bg-amber-50 dark:bg-amber-900/30">
              <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Updated</p>
              <p className="text-base font-medium">{timeInfo.lastUpdated}</p>
            </div>
          </div>
          
          {/* Work Document Analysis */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border">
            <div className="p-2 rounded-full bg-green-50 dark:bg-green-900/30">
              <Briefcase className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Work Documents</p>
              <p className="text-base font-medium">{workInfo.docCount} ({workInfo.workPercentage}%)</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AttachmentInsights;
